import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import { Extension } from '@tiptap/core'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import TextAlign from '@tiptap/extension-text-align'
import api from '../utils/api'
import { useAuth } from '../context/AuthContext'
import {
    listCaseManagerTemplates,
    getCaseManagerTemplate,
    saveCaseManagerTemplate,
    resetCaseManagerTemplate,
    sendCaseManagerTestEmail,
    listMediatorTemplates,
    getMediatorTemplate,
    saveMediatorTemplate,
    resetMediatorTemplate,
    sendMediatorTestEmail,
} from '../services/EmailTemplateService'
import { MergeTagNode, setMergeTagsRef } from '../components/email-editor/MergeTagNode'
import { ButtonBlockNode } from '../components/email-editor/ButtonBlockNode'
import EditorToolbar from '../components/email-editor/EditorToolbar'
import MergeTagSidebar from '../components/email-editor/MergeTagSidebar'
import SubjectInput from '../components/email-editor/SubjectInput'
import { htmlToTiptap, tiptapToHtml } from '../components/email-editor/htmlConversion'
import { applyInlineEmailStyles, EMAIL_BODY_CSS } from '../components/email-editor/emailStyles'

// Map Shift+Enter to the same command Enter runs (splitBlock = new paragraph).
// Combined with `hardBreak: false` in StarterKit, this makes Enter and
// Shift+Enter produce identical output everywhere — editor, preview, and
// sent email. No <br> tags are ever emitted.
const ShiftEnterSplitBlock = Extension.create({
    name: 'shiftEnterSplitBlock',
    addKeyboardShortcuts() {
        return {
            'Shift-Enter': () => this.editor.commands.splitBlock(),
        }
    },
})

// Merge tags come exclusively from scale-be's response now (same source
// admin + portal use). The previous local MERGE_TAG_LABELS +
// ALLOWED_TAGS_BY_SLUG fallback was deleted — keeping it would
// re-introduce drift the moment scale-be's spec changes.

// ============================================================================
// SAMPLE_DATA — used for preview rendering before real merge data is
// substituted. Matches Scale's SAMPLE_DATA where applicable so previews
// look the same in both apps.
// ============================================================================
const SAMPLE_DATA = {
    name: 'Jane Smith',
    caseTitle: 'Smith vs. Johnson Corp',
    caseName: 'Smith vs. Johnson Corp',
    caseNumber: 'SM-2026-0042',
    dateAndTime: 'April 15th, 2026 at 10:00 AM PST',
    zoomLink: 'https://zoom.us/j/123456789',
    zoomUrl: 'https://zoom.us/j/123456789',
    total: '$2,500.00',
    totalDue: '$2,500.00',
    dueDate: 'April 10th, 2026',
    onboardingLink: 'https://app.scalemediation.com/onboarding/abc123',
    onboardingUrl: 'https://app.scalemediation.com/onboarding/abc123',
    paymentLink: 'https://app.scalemediation.com/pay/abc123',
    paymentUrl: 'https://app.scalemediation.com/pay/abc123',
    invoiceLink: 'https://app.scalemediation.com/invoice/abc123',
    mediatorName: 'Sample Mediator',
    caseManagerName: 'Sample Case Manager',
    caseManagerEmail: 'casemanager@example.com',
    caseManagerPhone: '555-123-4567',
    clientType: 'Plaintiff',
    briefDeadline: 'April 12th, 2026',
}

// ============================================================================
// EmailTemplateEditor — the editor for a single template
// ============================================================================
function EmailTemplateEditor({ caseManagerId, mediatorId, slug, templateMeta, onBack, onSaved }) {
    // Pick the right BE service based on which audience is being edited.
    // When mediatorId is set, the case manager is editing a mediator's
    // templates on their behalf (uses scale-be /email-templates).
    // Otherwise the CM edits their own templates (uses /case-manager).
    const isMediatorScope = !!mediatorId
    const fetchTpl = (s) =>
        isMediatorScope
            ? getMediatorTemplate(mediatorId, s)
            : getCaseManagerTemplate(caseManagerId, s)
    const saveTpl = (s, payload) =>
        isMediatorScope
            ? saveMediatorTemplate(mediatorId, s, payload)
            : saveCaseManagerTemplate(caseManagerId, s, payload)
    const resetTpl = (s) =>
        isMediatorScope
            ? resetMediatorTemplate(mediatorId, s)
            : resetCaseManagerTemplate(caseManagerId, s)
    const sendTest = (s, email, subj, body) =>
        isMediatorScope
            ? sendMediatorTestEmail(mediatorId, s, email, subj, body)
            : sendCaseManagerTestEmail(caseManagerId, s, email, subj, body)

    // Pre-fill the "Send Test Email" field with the logged-in case
    // manager's address so they don't have to retype it every time —
    // matches Scale Portal's UX (which pre-fills with the mediator email).
    const { caseManager } = useAuth()
    const [isLoading, setIsLoading] = useState(true)
    const [isSaving, setIsSaving] = useState(false)
    const [templateData, setTemplateData] = useState(templateMeta || null)
    const [subject, setSubject] = useState('')
    const [body, setBody] = useState('')
    const [activeTab, setActiveTab] = useState('editor')
    const [saveStatus, setSaveStatus] = useState(null)
    const [statusMessage, setStatusMessage] = useState(null) // { type: 'success'|'error', message }
    const [mergeTags, setMergeTags] = useState([])
    const [showUnsavedModal, setShowUnsavedModal] = useState(false)
    const [testEmail, setTestEmail] = useState(caseManager?.email || '')
    const [isSendingTest, setIsSendingTest] = useState(false)
    const [showTestEmailModal, setShowTestEmailModal] = useState(false)
    const lastEmittedRef = useRef('')
    const savedSubjectRef = useRef('')
    const savedBodyRef = useRef('')
    const lastFocusedField = useRef('body')
    const subjectInsertRef = useRef(null)

    const previewData = SAMPLE_DATA

    const renderPreview = (html) => {
        if (!html) return ''
        let rendered = html
        Object.entries(previewData).forEach(([key, value]) => {
            rendered = rendered.replaceAll(`{{${key}}}`, value)
        })
        return rendered
    }

    const editor = useEditor({
        extensions: [
            StarterKit.configure({ heading: { levels: [1, 2, 3] }, hardBreak: false }),
            ShiftEnterSplitBlock,
            Underline,
            TextAlign.configure({ types: ['heading', 'paragraph'] }),
            Link.configure({ openOnClick: false, autolink: false, HTMLAttributes: { rel: 'noopener noreferrer' } }),
            MergeTagNode.configure({ mergeTags }),
            ButtonBlockNode,
        ],
        content: '<p></p>',
        onUpdate({ editor }) {
            const html = tiptapToHtml(editor.getHTML())
            lastEmittedRef.current = html
            setBody(html)
        },
        editorProps: {
            attributes: { class: 'focus:outline-none' },
        },
    })

    // Keep the shared merge tags ref in sync so pills render correct labels.
    useEffect(() => {
        setMergeTagsRef(mergeTags)
        if (editor) {
            editor.view.dispatch(editor.state.tr)
        }
    }, [mergeTags, editor])

    useEffect(() => {
        const fetchTemplate = async () => {
            setIsLoading(true)
            try {
                // Scale-be response shape: { success, data: {…template fields},
                // merge_tags: [...] }. merge_tags lives at the top level, not
                // nested inside data — same shape scalemediation-Admin reads.
                const response = await fetchTpl(slug)
                const data = response?.data
                setTemplateData(data)
                if (Array.isArray(response?.merge_tags)) {
                    setMergeTags(response.merge_tags)
                } else {
                    setMergeTags([])
                }
                const loadedSubject = data?.subject || ''
                const loadedBody = data?.body_html || ''
                setSubject(loadedSubject)
                savedSubjectRef.current = loadedSubject
                if (editor) {
                    editor.commands.setContent(htmlToTiptap(loadedBody || '<p></p>'), false)
                    const normalizedBody = tiptapToHtml(editor.getHTML())
                    savedBodyRef.current = normalizedBody
                    lastEmittedRef.current = normalizedBody
                    setBody(normalizedBody)
                } else {
                    savedBodyRef.current = loadedBody
                    lastEmittedRef.current = loadedBody
                    setBody(loadedBody)
                }
            } catch (err) {
                console.error('Error fetching template:', err)
                setStatusMessage({ type: 'error', message: 'Failed to load template' })
            } finally {
                setIsLoading(false)
            }
        }
        if (editor) fetchTemplate()
        // Intentionally not depending on every state setter — slug + editor
        // identity are the only triggers we want.
        // eslint-disable-next-line
    }, [slug, caseManagerId, mediatorId, editor])

    const handleInsertTag = (tagName) => {
        if (lastFocusedField.current === 'subject' && subjectInsertRef.current) {
            subjectInsertRef.current(tagName)
        } else if (editor) {
            const { from, to } = editor.state.selection
            if (from !== to) {
                editor.chain().focus().deleteSelection().insertMergeTag({ tag: tagName }).run()
            } else {
                editor.chain().focus().insertMergeTag({ tag: tagName }).run()
            }
        }
    }

    const handleInsertButton = (text, url) => {
        if (editor) {
            editor.chain().focus().insertContent({ type: 'buttonBlock', attrs: { text, url } }).run()
        }
    }

    const handleSendTest = async () => {
        if (!testEmail || !testEmail.includes('@')) {
            setStatusMessage({ type: 'error', message: 'Please enter a valid email address' })
            return
        }
        setIsSendingTest(true)
        try {
            await sendTest(slug, testEmail, subject, body)
            setStatusMessage({ type: 'success', message: `Test email sent to ${testEmail}` })
            setShowTestEmailModal(false)
        } catch (err) {
            console.error('Error sending test email:', err)
            setStatusMessage({ type: 'error', message: 'Failed to send test email' })
        } finally {
            setIsSendingTest(false)
        }
    }

    const handleEditorDrop = useCallback((e) => {
        const tagData = e.dataTransfer.getData('application/x-merge-tag')
        const buttonData = e.dataTransfer.getData('application/x-button-block')

        if (!tagData && !buttonData) return

        e.preventDefault()
        e.stopPropagation()

        if (!editor) return

        const pos = editor.view.posAtCoords({ left: e.clientX, top: e.clientY })
        if (pos) {
            editor.chain().focus().setTextSelection(pos.pos).run()
        }

        if (tagData) {
            editor.chain().focus().insertMergeTag({ tag: tagData }).run()
        } else if (buttonData) {
            try {
                const { text, url } = JSON.parse(buttonData)
                editor.chain().focus().insertContent({ type: 'buttonBlock', attrs: { text, url } }).run()
            } catch (err) {
                console.error('Invalid button drag data:', err)
            }
        }
    }, [editor])

    const handleEditorDragOver = useCallback((e) => {
        if (e.dataTransfer.types.includes('application/x-merge-tag') || e.dataTransfer.types.includes('application/x-button-block')) {
            e.preventDefault()
            e.dataTransfer.dropEffect = 'copy'
        }
    }, [])

    const handleSave = async () => {
        if (!editor) return false
        setIsSaving(true)
        try {
            await saveTpl(slug, {
                subject,
                body_html: body,
            })
            savedSubjectRef.current = subject
            savedBodyRef.current = body
            setSaveStatus('saved')
            setTimeout(() => setSaveStatus(null), 3000)
            setTemplateData((prev) => prev ? { ...prev, status: 'customized' } : prev)
            setStatusMessage({ type: 'success', message: 'Template saved' })
            if (onSaved) onSaved()
            return true
        } catch (err) {
            console.error('Error saving template:', err)
            setStatusMessage({ type: 'error', message: 'Failed to save template' })
            return false
        } finally {
            setIsSaving(false)
        }
    }

    const handleReset = async () => {
        if (!window.confirm('Reset this template to default? Your customizations will be lost.')) return
        try {
            await resetTpl(slug)
            const freshRes = await fetchTpl(slug)
            const freshData = freshRes?.data
            const defaultBody = freshData?.body_html || templateData?.default_body_html || ''
            const defaultSubject = freshData?.subject || templateData?.default_subject || ''
            setSubject(defaultSubject)
            savedSubjectRef.current = defaultSubject
            if (editor) {
                editor.commands.setContent(htmlToTiptap(defaultBody || '<p></p>'), false)
                const normalizedBody = tiptapToHtml(editor.getHTML())
                savedBodyRef.current = normalizedBody
                lastEmittedRef.current = normalizedBody
                setBody(normalizedBody)
            } else {
                savedBodyRef.current = defaultBody
                lastEmittedRef.current = defaultBody
                setBody(defaultBody)
            }
            setTemplateData((prev) => prev ? { ...prev, status: 'default' } : prev)
            setStatusMessage({ type: 'success', message: 'Template reset to default' })
            if (onSaved) onSaved()
        } catch (err) {
            console.error('Error resetting template:', err)
            setStatusMessage({ type: 'error', message: 'Failed to reset template' })
        }
    }

    const hasUnsavedChanges = subject !== savedSubjectRef.current || body !== savedBodyRef.current

    const handleBack = () => {
        if (hasUnsavedChanges) {
            setShowUnsavedModal(true)
        } else {
            onBack()
        }
    }

    // Auto-dismiss status messages after 4s
    useEffect(() => {
        if (!statusMessage) return
        const t = setTimeout(() => setStatusMessage(null), 4000)
        return () => clearTimeout(t)
    }, [statusMessage])

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
            </div>
        )
    }

    const isCustomized = templateData?.status === 'customized'
    const previewHtml = applyInlineEmailStyles(renderPreview(body))

    return (
        <div
            onClickCapture={(e) => {
                const target = e.target.closest('button')
                if (target && !target.getAttribute('type')) {
                    target.setAttribute('type', 'button')
                }
            }}
            className="flex flex-col bg-white rounded-lg border border-gray-200 overflow-hidden"
            style={{ minHeight: '70vh' }}
        >
            <style>{EMAIL_BODY_CSS}</style>

            {/* Sub-header */}
            <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-2.5">
                    <button
                        type="button"
                        onClick={handleBack}
                        className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 transition-colors cursor-pointer"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        Templates
                    </button>
                    <span className="text-gray-300">/</span>
                    <span className="text-sm font-medium text-gray-900">{templateData?.name}</span>
                    {isCustomized ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                            <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                            Customized
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200">
                            <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                            Default
                        </span>
                    )}
                </div>

                <div className="flex items-center gap-2">
                    {saveStatus === 'saved' && (
                        <span className="text-xs text-green-600 flex items-center gap-1">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Saved
                        </span>
                    )}
                    <button
                        type="button"
                        onClick={() => setShowTestEmailModal(true)}
                        disabled={isSendingTest}
                        className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white hover:bg-gray-50 border border-gray-200 rounded-md transition-colors cursor-pointer disabled:opacity-60 flex items-center gap-1"
                    >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        {isSendingTest ? 'Sending…' : 'Send Test Email'}
                    </button>
                    {isCustomized && (
                        <button
                            type="button"
                            onClick={handleReset}
                            className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white hover:bg-gray-50 border border-gray-200 rounded-md transition-colors cursor-pointer"
                        >
                            Reset to Default
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={isSaving}
                        className="px-4 py-1.5 text-xs font-medium text-white disabled:opacity-60 rounded-md transition-colors cursor-pointer flex items-center gap-1.5"
                        style={{ backgroundColor: '#115E59' }}
                    >
                        {isSaving ? 'Saving…' : 'Save'}
                    </button>
                </div>
            </div>

            {/* Subject line */}
            <div className="bg-white border-b border-gray-200 px-6 py-2.5 flex items-center gap-3 shrink-0">
                <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">
                    Subject
                </label>
                <SubjectInput
                    value={subject}
                    onChange={setSubject}
                    mergeTags={mergeTags}
                    placeholder="Email subject line…"
                    onFocus={() => { lastFocusedField.current = 'subject' }}
                    insertTagRef={subjectInsertRef}
                />
            </div>

            {/* Status message */}
            {statusMessage && (
                <div
                    className={`px-6 py-2 text-xs border-b shrink-0 ${
                        statusMessage.type === 'success'
                            ? 'bg-green-50 border-green-200 text-green-700'
                            : 'bg-red-50 border-red-200 text-red-700'
                    }`}
                >
                    {statusMessage.message}
                </div>
            )}

            {/* Workspace */}
            <div className="flex flex-1 min-h-0 overflow-hidden">
                <MergeTagSidebar mergeTags={mergeTags} onInsertTag={handleInsertTag} onInsertButton={handleInsertButton} />

                <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
                    {/* Tab bar */}
                    <div className="bg-white border-b border-gray-200 px-4 flex items-center gap-0 shrink-0">
                        {['editor', 'preview'].map((tab) => (
                            <button
                                type="button"
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className="px-4 py-2.5 text-xs font-medium border-b-2 transition-colors cursor-pointer capitalize"
                                style={{
                                    borderColor: activeTab === tab ? '#115E59' : 'transparent',
                                    color: activeTab === tab ? '#115E59' : '#6b7280',
                                }}
                            >
                                {tab === 'editor' ? 'Editor' : 'Preview'}
                            </button>
                        ))}
                    </div>

                    {/* Editor pane */}
                    <div
                        className="flex-1 flex flex-col min-h-0 overflow-hidden"
                        style={{ display: activeTab === 'editor' ? 'flex' : 'none' }}
                    >
                        <EditorToolbar editor={editor} />
                        <div
                            className="flex-1 overflow-auto bg-gray-100 py-8 px-4"
                            onDrop={handleEditorDrop}
                            onDragOver={handleEditorDragOver}
                        >
                            <div className="mx-auto bg-white shadow rounded-sm" style={{ maxWidth: 600 }}>
                                <div className="email-body-content px-10 py-8" onFocus={() => { lastFocusedField.current = 'body' }}>
                                    <EditorContent editor={editor} />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Preview pane */}
                    <div
                        className="flex-1 overflow-auto bg-gray-100"
                        style={{ display: activeTab === 'preview' ? 'block' : 'none' }}
                    >
                        <div className="max-w-2xl mx-auto my-6 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                            <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                    <span className="font-semibold text-gray-600">Subject:</span>
                                    <span className="text-gray-800">{renderPreview(subject)}</span>
                                </div>
                            </div>
                            <div
                                className="email-body-content px-8 py-6"
                                dangerouslySetInnerHTML={{ __html: previewHtml }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Unsaved changes modal */}
            {showUnsavedModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                    <div className="bg-white rounded-lg shadow-xl max-w-sm w-full mx-4 p-6">
                        <h3 className="text-sm font-semibold text-gray-900 mb-2">Unsaved Changes</h3>
                        <p className="text-xs text-gray-500 mb-5">
                            You have unsaved changes. Would you like to save before leaving?
                        </p>
                        <div className="flex items-center justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => {
                                    setShowUnsavedModal(false)
                                    onBack()
                                }}
                                className="px-3 py-1.5 text-xs font-medium text-red-600 bg-white hover:bg-red-50 border border-gray-200 rounded-md transition-colors cursor-pointer"
                            >
                                Discard
                            </button>
                            <button
                                type="button"
                                onClick={() => setShowUnsavedModal(false)}
                                className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white hover:bg-gray-50 border border-gray-200 rounded-md transition-colors cursor-pointer"
                            >
                                Keep Editing
                            </button>
                            <button
                                type="button"
                                onClick={async () => {
                                    const saved = await handleSave()
                                    if (!saved) return
                                    setShowUnsavedModal(false)
                                    onBack()
                                }}
                                className="px-4 py-1.5 text-xs font-medium text-white rounded-md transition-colors cursor-pointer"
                                style={{ backgroundColor: '#115E59' }}
                            >
                                Save & Leave
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Send Test Email modal */}
            {showTestEmailModal && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
                    onClick={(e) => { if (e.target === e.currentTarget) setShowTestEmailModal(false) }}
                >
                    <div className="bg-white rounded-lg shadow-xl w-[420px] mx-4">
                        <div className="p-6">
                            <h3 className="text-base font-semibold text-gray-900 mb-1">Send Test Email</h3>
                            <p className="text-sm text-gray-500 mb-5">
                                Send a preview of this template with sample data.
                            </p>
                            <label className="block text-xs font-medium text-gray-700 mb-1.5">Recipient Email</label>
                            <input
                                type="email"
                                value={testEmail}
                                onChange={(e) => setTestEmail(e.target.value)}
                                placeholder="Enter email address"
                                autoFocus
                                onKeyDown={(e) => { if (e.key === 'Enter') handleSendTest() }}
                                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md outline-none focus:border-[#115E59] focus:ring-1 focus:ring-[#115E59] transition-colors"
                            />
                            <div className="flex items-center justify-end gap-2 mt-6">
                                <button
                                    type="button"
                                    onClick={() => setShowTestEmailModal(false)}
                                    className="px-4 py-2 text-sm font-medium text-gray-600 bg-white hover:bg-gray-50 border border-gray-200 rounded-md transition-colors cursor-pointer"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleSendTest}
                                    disabled={isSendingTest}
                                    className="px-5 py-2 text-sm font-medium text-white disabled:opacity-60 rounded-md transition-colors cursor-pointer flex items-center gap-1.5"
                                    style={{ backgroundColor: '#115E59' }}
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                    {isSendingTest ? 'Sending…' : 'Send'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

// ============================================================================
// EmailTemplates — page-level component (list view + editor)
// ============================================================================
export default function EmailTemplates() {
    const { caseManager } = useAuth()
    const [templates, setTemplates] = useState([])
    const [loading, setLoading] = useState(true)
    const [selectedSlug, setSelectedSlug] = useState(null)
    const [selectedMeta, setSelectedMeta] = useState(null)

    // Mediator-filter state. Null = "Case Manager (me)" — the page
    // shows the CM's own templates. Set to a mediator id = act on the
    // mediator's behalf, showing their templates from scale-be.
    const [mediators, setMediators] = useState([])
    const [selectedMediatorId, setSelectedMediatorId] = useState(null)
    const [mediatorDropdownOpen, setMediatorDropdownOpen] = useState(false)
    // Inline search inside the mediator dropdown — keeps the picker usable
    // when a CM manages many mediators.
    const [mediatorQuery, setMediatorQuery] = useState('')
    const mediatorDropdownRef = useRef(null)

    // Close the mediator dropdown when clicking outside it. Same pattern
    // would be hand-rolled on Signatures.jsx etc., extracted here so
    // selecting an option or clicking elsewhere both dismiss the panel.
    useEffect(() => {
        if (!mediatorDropdownOpen) return
        const handler = (e) => {
            if (
                mediatorDropdownRef.current &&
                !mediatorDropdownRef.current.contains(e.target)
            ) {
                setMediatorDropdownOpen(false)
            }
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [mediatorDropdownOpen])

    const activeCMId = caseManager?.id

    // Pull the case manager's assigned mediators from cm-be (same
    // endpoint Schedule/Cases/Signatures/Billing already use). The
    // BE filters to the logged-in CM's assignments via the auth
    // middleware's attachCaseManager step.
    useEffect(() => {
        let cancelled = false
        api.get('/mediators?limit=200')
            .then((res) => {
                if (cancelled) return
                const list = res?.data?.data || res?.data || []
                setMediators(Array.isArray(list) ? list : [])
            })
            .catch(() => {
                if (!cancelled) setMediators([])
            })
        return () => {
            cancelled = true
        }
    }, [])

    const fetchTemplates = useCallback(async () => {
        if (!activeCMId && !selectedMediatorId) return
        setLoading(true)
        try {
            // Branch based on which audience is selected. CM mode hits
            // /case-manager/:cmId/email-templates; Mediator mode hits
            // /email-templates?mediator_id=X (same endpoint scale-portal uses).
            const body = selectedMediatorId
                ? await listMediatorTemplates(selectedMediatorId)
                : await listCaseManagerTemplates(activeCMId)
            if (body?.success) setTemplates(body.data || [])
            else setTemplates([])
        } catch (err) {
            console.error('Failed to fetch templates:', err)
            setTemplates([])
        } finally {
            setLoading(false)
        }
    }, [activeCMId, selectedMediatorId])

    useEffect(() => { fetchTemplates() }, [fetchTemplates])

    if (selectedSlug) {
        return (
            <div className="p-6">
                <EmailTemplateEditor
                    caseManagerId={selectedMediatorId ? null : activeCMId}
                    mediatorId={selectedMediatorId || null}
                    slug={selectedSlug}
                    templateMeta={selectedMeta}
                    onBack={() => { setSelectedSlug(null); setSelectedMeta(null) }}
                    onSaved={fetchTemplates}
                />
            </div>
        )
    }

    const mediatorLabel = (m) => {
        const first = m?.first_name || m?.firstName || ''
        const last = m?.last_name || m?.lastName || ''
        const full = `${first} ${last}`.trim()
        return full || m?.name || m?.email || `Mediator #${m?.id}`
    }

    return (
        <div className="p-6">
            {/* List header — original case-manager design: text-xl title,
                text-sm description, mb-6. The dropdown filter sits on
                the right; subtitle copy switches based on whether the
                CM is editing their own templates or a mediator's. */}
            <div className="flex items-center justify-between mb-6 gap-3">
                <div>
                    <h1 className="text-xl font-semibold text-gray-900">Case Manager Email Templates</h1>
                    <p className="text-sm text-gray-500 mt-1">
                        {selectedMediatorId
                            ? 'Editing this mediator’s templates on their behalf.'
                            : 'Customize the emails your case manager identity sends to mediators and clients.'}
                    </p>
                </div>
                {/* Mediator filter — defaults to the CM's own templates,
                    pick a mediator to act on their behalf. Hand-rolled
                    React dropdown (not native <select>) so the styling
                    matches the rest of the cm-fe pages — same pattern
                    as Signatures.jsx's mediator/status filters. No
                    standalone label: the button itself shows the
                    current scope (matches Signatures.jsx). */}
                <div className="shrink-0">
                    <div className="relative" ref={mediatorDropdownRef}>
                        <button
                            type="button"
                            onClick={() => setMediatorDropdownOpen((v) => !v)}
                            aria-haspopup="listbox"
                            aria-expanded={mediatorDropdownOpen}
                            className={`flex items-center justify-between gap-3 h-9 min-w-[220px] px-3 rounded-lg border bg-white transition-colors ${
                                selectedMediatorId
                                    ? 'border-[#115e59] bg-[#f0fdfa]'
                                    : 'border-[#e5e5e5] hover:border-gray-300'
                            }`}
                        >
                            <span
                                className="text-[13px] font-medium text-[#0e1e20] truncate"
                                style={{ fontFamily: 'Inter' }}
                            >
                                {selectedMediatorId
                                    ? mediatorLabel(
                                          mediators.find((m) => m.id === selectedMediatorId) ||
                                              {},
                                      )
                                    : 'Case Manager (me)'}
                            </span>
                            <svg
                                width="12"
                                height="12"
                                viewBox="0 0 12 12"
                                fill="none"
                                aria-hidden="true"
                                className={`transition-transform ${
                                    mediatorDropdownOpen ? 'rotate-180' : ''
                                }`}
                            >
                                <path
                                    d="M3 4.5L6 7.5L9 4.5"
                                    stroke="#0e1e20"
                                    strokeWidth="1.2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </button>
                        {mediatorDropdownOpen && (() => {
                            const q = mediatorQuery.trim().toLowerCase()
                            const filtered = q
                                ? mediators.filter((m) => (mediatorLabel(m) || '').toLowerCase().includes(q))
                                : mediators
                            return (
                                <div
                                    role="listbox"
                                    className="absolute top-full right-0 mt-1 w-[260px] bg-white border border-[#e5e5e5] rounded-lg shadow-lg z-50 flex flex-col"
                                    style={{ maxHeight: 360 }}
                                >
                                    <div className="p-2 border-b border-[#e5e5e5]">
                                        <div className="relative">
                                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none">
                                                <path d="M6.417 11.083A4.667 4.667 0 106.417 1.75a4.667 4.667 0 000 9.333zM12.25 12.25l-2.537-2.537" stroke="#9ca3af" strokeWidth="1.167" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                            <input
                                                type="text"
                                                placeholder="Search mediators..."
                                                value={mediatorQuery}
                                                onChange={(e) => setMediatorQuery(e.target.value)}
                                                autoFocus
                                                className="w-full h-8 pl-8 pr-2 rounded-md border border-[#e5e5e5] bg-white text-[13px] text-[#0e1e20] placeholder-[#9ca3af] outline-none focus:border-[#115e59] focus:ring-1 focus:ring-[#115e59]/20"
                                                style={{ fontFamily: 'Inter' }}
                                            />
                                        </div>
                                    </div>
                                    <div className="overflow-auto flex-1">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setSelectedMediatorId(null)
                                                setSelectedSlug(null)
                                                setSelectedMeta(null)
                                                setMediatorDropdownOpen(false)
                                                setMediatorQuery('')
                                            }}
                                            className={`w-full text-left px-3 py-2 text-[13px] hover:bg-[#f5f5f5] ${
                                                !selectedMediatorId
                                                    ? 'bg-[#e8fdf5] text-[#115e59] font-medium'
                                                    : 'text-[#333]'
                                            }`}
                                            style={{ fontFamily: 'Inter' }}
                                        >
                                            Case Manager (me)
                                        </button>
                                        {filtered.length > 0 && (
                                            <>
                                                <div
                                                    className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wide text-gray-400"
                                                    style={{ fontFamily: 'Inter' }}
                                                >
                                                    My Mediators
                                                </div>
                                                {filtered.map((m) => (
                                                    <button
                                                        type="button"
                                                        key={m.id}
                                                        onClick={() => {
                                                            setSelectedMediatorId(m.id)
                                                            setSelectedSlug(null)
                                                            setSelectedMeta(null)
                                                            setMediatorDropdownOpen(false)
                                                            setMediatorQuery('')
                                                        }}
                                                        className={`w-full text-left px-3 py-2 text-[13px] hover:bg-[#f5f5f5] ${
                                                            selectedMediatorId === m.id
                                                                ? 'bg-[#e8fdf5] text-[#115e59] font-medium'
                                                                : 'text-[#333]'
                                                        }`}
                                                        style={{ fontFamily: 'Inter' }}
                                                    >
                                                        {mediatorLabel(m)}
                                                    </button>
                                                ))}
                                            </>
                                        )}
                                        {filtered.length === 0 && mediators.length > 0 && (
                                            <div className="px-3 py-3 text-[12px] text-[#9ca3af]" style={{ fontFamily: 'Inter' }}>
                                                No mediators found
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )
                        })()}
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="flex items-center justify-center h-40">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
                </div>
            ) : templates.length === 0 ? (
                <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-sm text-gray-500">
                    No templates available.
                </div>
            ) : (
                <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
                    {templates.map((t) => (
                        <div
                            key={t.slug}
                            className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors cursor-pointer"
                            onClick={() => { setSelectedSlug(t.slug); setSelectedMeta(t) }}
                        >
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <h3 className="text-sm font-medium text-gray-900">{t.name}</h3>
                                    {t.status === 'customized' ? (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                                            <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                                            Customized
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200">
                                            <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                                            Default
                                        </span>
                                    )}
                                </div>
                                {t.description && <p className="text-xs text-gray-500 mt-0.5">{t.description}</p>}
                                {t.subject && <p className="text-xs text-gray-400 mt-0.5 truncate">Subject: {t.subject}</p>}
                            </div>
                            <button
                                type="button"
                                className="ml-4 inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white rounded-lg transition-colors"
                                style={{ backgroundColor: '#115E59' }}
                            >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                Edit
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}
